import { useState } from 'react'
import {
  Box, Button, Chip, CircularProgress,
  MenuItem, Stack, Typography,
} from '@mui/material'
import {
  Phone as PhoneIcon,
  AccessTime as DurationIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material'
import {
  CallLog, CallStatus, CallOutcome,
  CALL_STATUS_LABELS, CALL_OUTCOME_LABELS,
  CALL_STATUS_COLORS, CALL_OUTCOME_COLORS,
  formatDuration,
} from '../../lib/api/callLogsApi'
import { EntityModal } from '../shared/EntityModal'
import { themeColors } from '../../app/layout/theme'
import { FormInput, FormSelect } from '../ui'

const STATUS_OPTIONS: { value: CallStatus; label: string }[] = [
  { value: 'CONNECTED', label: 'Connected — spoke with lead' },
  { value: 'NOT_ANSWERED', label: 'Not answered' },
  { value: 'BUSY', label: 'Busy / will call back' },
  { value: 'FAILED', label: 'Call failed' },
]

const OUTCOME_OPTIONS: { value: CallOutcome; label: string; desc: string }[] = [
  { value: 'INTERESTED', label: 'Interested', desc: 'Lead expressed clear interest' },
  { value: 'NOT_INTERESTED', label: 'Not Interested', desc: 'Lead declined the offer' },
  { value: 'FOLLOW_UP', label: 'Follow-up Required', desc: 'Needs a callback or more info' },
  { value: 'WRONG_NUMBER', label: 'Wrong Number', desc: 'Incorrect contact number' },
  { value: 'CALLBACK', label: 'Callback Requested', desc: 'Lead asked us to call back' },
  { value: 'NO_RESPONSE', label: 'No Response', desc: 'Line connected but no reply' },
]

interface CallOutcomeModalProps {
  call: CallLog
  onSave: (data: {
    status: CallStatus
    outcome: CallOutcome
    notes: string
    followUpDate: string | null
  }) => void
  isSaving: boolean
  /** When true the call status was set by the Knowlarity webhook via SignalR and must not be changed. */
  statusLockedByWebhook?: boolean
}

export function CallOutcomeModal({ call, onSave, isSaving, statusLockedByWebhook = false }: CallOutcomeModalProps) {
  const [status, setStatus] = useState<CallStatus>(call.status as CallStatus || 'CONNECTED')
  const [outcome, setOutcome] = useState<CallOutcome | ''>('')
  const [notes, setNotes] = useState('')
  const [followUpDate, setFollowUpDate] = useState('')
  const [errors, setErrors] = useState<{ status?: string; outcome?: string }>({})

  const durationLabel = formatDuration(call.durationSeconds)

  const validate = (): boolean => {
    const e: { status?: string; outcome?: string } = {}
    if (!status) e.status = 'Please select a call status'
    if (!outcome) e.outcome = 'Please select an outcome'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    onSave({
      status,
      outcome: outcome as CallOutcome,
      notes: notes.trim(),
      followUpDate: followUpDate ? new Date(followUpDate).toISOString() : null,
    })
  }

  const getCallStatusExplanation = () => {
    const mainStatus = call.status?.toLowerCase().trim() || 'none'
    const agent = call.agentCallStatus?.toLowerCase().trim() || 'none'
    const customer = call.customerCallStatus?.toLowerCase().trim() || 'none'

    const isNoneOrNotConnected = (val: string) => 
      val === 'none' || val === 'not connected' || val === 'not_connected'

    // 1. Call hanged by customer
    // Status: missed, Agent Status: connected, Customer Status: missed
    if (
      mainStatus === 'missed' &&
      agent === 'connected' &&
      customer === 'missed'
    ) {
      return { 
        text: '📞 Call hanged by customer.', 
        color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' 
      }
    }

    // 2. Call missed by agent
    // Status: none, Agent Status: none/not_connected, Customer Status: not connected
    if (
      isNoneOrNotConnected(mainStatus) &&
      isNoneOrNotConnected(agent) &&
      (customer === 'not connected' || customer === 'not_connected')
    ) {
      return { 
        text: '⚠️ Call missed by agent.', 
        color: '#92400e', bg: '#fef3c7', border: '#fde68a' 
      }
    }

    // 3. Call hanged by agent
    // Status: none, Agent Status: not connected, Customer Status: none
    if (
      isNoneOrNotConnected(mainStatus) &&
      (agent === 'not connected' || agent === 'not_connected') &&
      isNoneOrNotConnected(customer)
    ) {
      return { 
        text: '🛑 Call hanged by agent.', 
        color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' 
      }
    }

    // 4. Call missed by customer
    // Status: missed, Agent Status: not connected, Customer Status: none
    if (
      mainStatus === 'missed' &&
      (agent === 'not connected' || agent === 'not_connected') &&
      isNoneOrNotConnected(customer)
    ) {
      return { 
        text: '🔔 Call missed by customer.', 
        color: '#92400e', bg: '#fef3c7', border: '#fde68a' 
      }
    }

    // 5. Call connected successfully
    if (agent === 'connected' && customer === 'connected') {
      return { 
        text: '✅ Call connected.', 
        color: '#15803d', bg: '#dcfce7', border: '#bbf7d0' 
      }
    }

    // Fallbacks
    if (customer === 'hanged' || customer === 'hangup') {
      return { text: '📞 Call disconnected: Hanged by Customer', color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' }
    }
    if (agent === 'hanged' || agent === 'hangup') {
      return { text: '🛑 Call disconnected: Hanged by Agent (You)', color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' }
    }
    if (customer === 'busy') {
      return { text: '⏳ Connection failed: Customer line was busy', color: '#92400e', bg: '#fef3c7', border: '#fde68a' }
    }
    if (customer === 'failed' || agent === 'failed') {
      return { text: '❌ Connection failed during call routing.', color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' }
    }

    return null
  }

  return (
    <EntityModal
      open
      onClose={() => { }}
      title="Log Call Outcome"
      icon={<PhoneIcon sx={{ fontSize: 18 }} />}
      maxWidth="sm"
      hideClose
      actions={
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography fontSize="11px" color="text.disabled" sx={{ flex: 1 }}>
            * Cannot be skipped — this ensures every call is tracked.
          </Typography>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              background: themeColors.primary,
              '&:hover': { background: themeColors.primaryDark },
            }}
          >
            {isSaving
              ? <><CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />Saving…</>
              : 'Save & Complete Call Log'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ mt: -1, mb: 2 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            mb: 2,
            borderRadius: '12px',
            background: themeColors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            color: '#fff'
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PhoneIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="14px">
              {call.leadName || call.contactName}
            </Typography>
            <Typography fontSize="12px" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {call.leadPhone || call.contactPhone}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DurationIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
            <Typography fontSize="13px" fontWeight={600}>
              {durationLabel}
            </Typography>
          </Box>
        </Box>

        {!statusLockedByWebhook && (
          <Box
            sx={{
              px: 2,
              py: 1,
              mb: 2,
              bgcolor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #fde68a',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography fontSize="11px" color="#92400e" fontWeight={600}>
              ⚠️ This form must be completed. Call log is not saved until you submit.
            </Typography>
          </Box>
        )}

        {(() => {
          const explanation = getCallStatusExplanation()
          if (!explanation) return null
          return (
            <Box
              sx={{
                px: 2,
                py: 1,
                mb: 2,
                bgcolor: explanation.bg,
                borderRadius: '8px',
                border: `1px solid ${explanation.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography fontSize="12px" color={explanation.color} fontWeight={600}>
                {explanation.text}
              </Typography>
            </Box>
          )
        })()}

        <Stack spacing={2.5}>
          {statusLockedByWebhook ? (
            /* Status was set by the Knowlarity webhook — show as read-only chip */
            <Box>
              <Typography fontSize="12px" color="text.secondary" fontWeight={500} mb={0.75}>
                Call Status
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '8px',
                  border: `1px solid`,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Chip
                  label={STATUS_OPTIONS.find(o => o.value === status)?.label ?? status}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '12px',
                    bgcolor:
                      status === 'CONNECTED' || status === 'ANSWERED' ? '#dcfce7' :
                      status === 'NOT_ANSWERED' || status === 'NO_ANSWER' || status === 'ABANDONED' ? '#fef3c7' :
                      status === 'BUSY' ? '#fee2e2' : '#f3f4f6',
                    color:
                      status === 'CONNECTED' || status === 'ANSWERED' ? '#15803d' :
                      status === 'NOT_ANSWERED' || status === 'NO_ANSWER' || status === 'ABANDONED' ? '#92400e' :
                      status === 'BUSY' ? '#b91c1c' : '#374151',
                  }}
                />
                <Typography fontSize="11px" color="text.disabled">
                  Auto-filled from call record
                </Typography>
              </Box>
            </Box>
          ) : (
            <FormSelect
              label="Call Status *"
              value={status}
              onChange={(v: any) => { setStatus(v?.value ?? 'CONNECTED'); setErrors(err => ({ ...err, status: '' })) }}
              error={!!errors.status}
              helperText={errors.status}
              fullWidth
              options={STATUS_OPTIONS}
            />
          )}

          <FormSelect
            label="Call Outcome *"
            value={outcome}
            onChange={(v: any) => { setOutcome(v?.value ?? ''); setErrors(err => ({ ...err, outcome: '' })) }}
            error={!!errors.outcome}
            helperText={errors.outcome}
            fullWidth
            options={OUTCOME_OPTIONS}
          />

          <FormInput
            label="Notes (optional)"
            placeholder="What was discussed? Key objections, next steps, pain points..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <CalendarIcon sx={{ fontSize: 15, color: '#6b7280' }} />
              <Typography fontSize="12px" color="text.secondary" fontWeight={500}>
                Schedule Follow-up (optional)
              </Typography>
            </Box>
            <FormInput
              fullWidth
              type="datetime-local"
              value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: (() => {
                const now = new Date()
                const tzOffset = now.getTimezoneOffset() * 60000
                return new Date(now.getTime() - tzOffset).toISOString().slice(0, 16)
              })() }}
              helperText={followUpDate ? 'A follow-up task will be automatically created.' : ''}
            />
          </Box>
        </Stack>
      </Box>
    </EntityModal>
  )
}
